import React, { useEffect, useState } from 'react';

import FixedWidthContainer from 'components/consumer/FixedWidthContainer';
import CardCarousel from 'components/consumer/image-slider-standard/components/CardCarousel';
import { fetchPostsByCategory } from 'lib/contentful-client';
import ToggleCTALinkButton from 'components/essentials/ToggleCTALinkButton';
import AdminPlaceholder from 'components/consumer/AdminPlaceholder';
import classNames from 'classnames';
import BlogSlideComponent from './BlogSlideComponent';

const blogUrl = '/blog/';

const ConsumerBlogSliderInner = ({ config, isAdmin }) => {
  const {
    toggleInputType,
    textContentSection,
    backgroundSelection,
    imageComponent,
  } = config;
  const [blogs, setBlogs] = useState([]);

  // Extract config-driven props
  const variant = 'default';
  const layoutSize = imageComponent?.size ?? 'Full Width';
  const textOrientation =
    textContentSection?.textOrientation?.toLowerCase() ?? 'center';
  const titleSize = textContentSection?.title?.size ?? 'text-2xl';
  const bodySize = textContentSection?.body?.size ?? 'text-base';
  const textColor = textContentSection?.textColor ?? 'black';
  const backgroundColor = backgroundSelection?.backgroundColor ?? '#FFFFFF';
  const titleText = textContentSection?.title?.text ?? '';
  const bodyText = textContentSection?.body?.text ?? '';
  const elementAttribute = { id: '' };
  const TitleTag = textContentSection?.title?.tag || 'h1';
  const BodyTag = textContentSection?.body?.tag || 'h2';

  useEffect(() => {
    if (isAdmin) {
      return;
    }

    fetchPostsByCategory(
      blogUrl,
      'the-latest',
      20,
      0,
      'fields.slug,fields.title,fields.featuredImage'
    )
      .then(result => {
        if (
          result?.posts &&
          Array.isArray(result.posts) &&
          result.posts.length > 0
        ) {
          const posts = result.posts.map(post => ({
            title: post.title,
            slug: post.slug,
            image: `https:${post.featuredImage.fields.file.url.replace(
              'downloads.ctfassets.net',
              'images.ctfassets.net'
            )}`,
          }));
          setBlogs(posts);
        } else {
          setBlogs([]);
        }
      })
      .catch(() => {
        setBlogs([]);
      });
  }, [isAdmin]);

  if (isAdmin) {
    return (
      <AdminPlaceholder className="!min-h-[500px]">
        Consumer Blog Slide: Contentful API Blog Component Placeholder for
        Variant Builder. <br />
        The component will display correctly in Consumer
      </AdminPlaceholder>
    );
  }

  const children = blogs.map(blog => (
    <BlogSlideComponent
      image={blog.image}
      title={blog.title}
      key={blog.slug}
      link={`${blog.slug}`}
    />
  ));

  return (
    <div
      className="min-h-[440px] mx-0 mt-6 [&_a]:no-underline [&_p]:text-lg [&_p]:text-gray [&_p]:mt-[22px] [&_p]:mb-6 [&_.slider-control-centerright]:!top-[35%] [&_.slider-control-centerleft]:!top-[35%] [&_.slider-control-centerleft]:!left-[3px] [&_.slider-control-centerright]:!right-[3px]"
      style={{
        backgroundColor: backgroundColor ?? '',
      }}
    >
      {(titleText || bodyText) && (
        <div
          className={classNames('w-full flex justify-center', {
            'px-5 md:px-[3.333vw] min-[1500px]:px-[50px]':
              variant === 'default' && layoutSize === 'Full Page',
            'items-start': textOrientation.startsWith('top'),
            'items-center':
              textOrientation.startsWith('center') ||
              textOrientation.startsWith('right') ||
              textOrientation.startsWith('left'),
            'items-end': textOrientation.startsWith('bottom'),
            'text-center justify-items-center': textOrientation.endsWith(
              'center'
            ),
            'text-left justify-items-start': textOrientation.endsWith('left'),
            'text-right justify-items-end': textOrientation.endsWith('right'),
          })}
        >
          <div
            id={elementAttribute?.id ?? ''}
            className={classNames('text-gray md:mx-auto md:mb-0', {
              'mt-[30px] px-5 md:px-[3.333vw] min-[1500px]:px-[50px] pt-5 md:pt-[3.333vw] min-[1500px]:pt-[50px]':
                variant === 'default',
              'px-0 py-0': variant !== 'default',
              'w-full':
                layoutSize === 'Full Width' || variant === 'ImageAndContent',
              'max-w-[1400px]':
                layoutSize === 'Full Page' && variant !== 'default',
              'w-full sm:w-[1400px]':
                layoutSize === 'Full Page' && variant === 'default',
              '!flex-row': textOrientation !== 'right',
              'content-start': textOrientation === 'left',
              'flex-row-reverse': textOrientation === 'right',
              'items-start': textOrientation === 'top-left',
              'items-start flex-row-reverse': textOrientation === 'top-right',
              'items-start justify-center': textOrientation === 'top-center',
              'items-end': textOrientation === 'bottom-left',
              'items-end flex-row-reverse': textOrientation === 'bottom-right',
              'items-end justify-center': textOrientation === 'bottom-center',
              'justify-center': textOrientation === 'center',
            })}
          >
            {titleText && (
              <TitleTag
                className={classNames('text-3xl font-bold mb-5 md:mb-[30px]', {
                  'text-3xl font-bold': titleSize === 'text-3xl',
                  'text-[clamp(1.75rem,5.2vw,2.5rem)] leading-[clamp(2.25rem,6.25vw,3rem)] tracking-[.0125rem] font-bold':
                    titleSize === 'text-4xl',
                  'text-xs': titleSize === 'text-xs',
                  'text-sm': titleSize === 'text-sm',
                  'text-base': titleSize === 'text-base',
                  'text-lg': titleSize === 'text-lg',
                  'text-xl': titleSize === 'text-xl',
                  'text-2xl': titleSize === 'text-2xl',
                })}
                style={{
                  color: textColor ?? '',
                }}
              >
                {titleText}
              </TitleTag>
            )}
            {bodyText && (
              <BodyTag
                className={classNames('text-base [&_a]:font-bold', {
                  'text-xs': bodySize === 'text-xs',
                  'text-sm': bodySize === 'text-sm',
                  'text-base': bodySize === 'text-base',
                  'text-lg': bodySize === 'text-lg',
                  'text-xl': bodySize === 'text-xl',
                  'text-2xl': bodySize === 'text-2xl',
                  'text-3xl': bodySize === 'text-3xl',
                })}
                style={{
                  color: textColor ?? '',
                }}
              >
                <div
                  className="[&_ul]:list-disc [&_ol]:list-[auto] [&_ul]:list-inside [&_ol]:list-inside [&_ul]:pl-4 [&_ol]:pl-4"
                  dangerouslySetInnerHTML={{ __html: bodyText }}
                />
              </BodyTag>
            )}
          </div>
        </div>
      )}

      <CardCarousel
        imageHeight={340}
        imageWidth={340}
        navType="Button"
        variant="blog-slider"
        visibleSlides={4}
      >
        {children}
      </CardCarousel>
      {!!toggleInputType && (
        <div className="max-[767px]:pl-5 max-[767px]:pr-5 w-full flex flex-row justify-center my-4">
          <ToggleCTALinkButton
            toggleInputType={toggleInputType}
            noHorizontalPadding
          />
        </div>
      )}
    </div>
  );
};

const ConsumerBlogSlider = ({ config, isAdmin }) => {
  const layoutSize = config?.imageComponent?.size ?? 'Full Width';

  return layoutSize === 'Full Width' ? (
    <div className="w-full overflow-x-hidden">
      <ConsumerBlogSliderInner config={config} isAdmin={isAdmin} />
    </div>
  ) : (
    <FixedWidthContainer noMobilePadding>
      <ConsumerBlogSliderInner config={config} isAdmin={isAdmin} />
    </FixedWidthContainer>
  );
};

export default ConsumerBlogSlider;
